/** Shared presentational helpers for the dial surfaces. */

import { firstGrapheme, truncateGraphemes } from "@keydials/dial-renderer";

/** Expand a #rgb/#rrggbb colour to an rgba() string at the given alpha. */
export function hexA(hex: string | undefined, alpha: number): string {
  const h = (hex ?? "#000000").replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    return `rgba(0,0,0,${alpha})`;
  }
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** One segment of a proportional status bar. */
export interface BarPart {
  key: string;
  colour: string;
  weight: number;
  label?: string;
}

/** RAG rollup colours for itemised segments/sections (matches the handoff). */
export type Rag = "Red" | "Amber" | "Green";
export const RAG_COLOUR: Record<Rag, string> = {
  Red: "#e7161b",
  Amber: "#f5a51c",
  Green: "#3eb034",
};
export const RAG_LABEL: Record<Rag, string> = {
  Red: "None complete",
  Amber: "Some complete",
  Green: "All complete",
};

/**
 * The RAG band's words for one section's items (KEY-753). A section that names its items ("appointments") reads
 * "No appointments complete" / "Some appointments complete" / "All appointments complete"; with no noun, or the
 * platform's own "items", it is exactly {@link RAG_LABEL}. Only the plural is read — pass a section's
 * `itemNouns(...)` (or anything with `many`).
 */
export function ragLabel(band: Rag, items?: { many: string } | null): string {
  const many = items?.many?.trim();
  if (!many || many === "items") {
    return RAG_LABEL[band];
  }
  return band === "Red" ? `No ${many} complete` : band === "Amber" ? `Some ${many} complete` : `All ${many} complete`;
}

/** A person rendered as an avatar. Derived from instance members (email/displayName/role). */
export interface Person {
  name: string;
  role?: string;
  initials: string;
  colour: string;
}

const AVATAR_COLOURS = ["#1e1d51", "#0e7490", "#7c3aed", "#b3532f", "#be123c", "#15803d", "#475569", "#a16207"];

/** Two-letter initials from a name or email local-part. */
export function initialsOf(nameOrEmail: string): string {
  const base = nameOrEmail.includes("@") ? nameOrEmail.split("@")[0] : nameOrEmail;
  const words = base.split(/[\s._-]+/).filter(Boolean);
  // Whole characters, so a name that starts with an emoji gets the emoji, not half of it (KEY-757).
  const letters = words.length >= 2 ? firstGrapheme(words[0]) + firstGrapheme(words[1]) : truncateGraphemes(base, 2);
  return letters.toUpperCase();
}

/** A readable display name from a member's displayName or email local-part. */
export function displayNameOf(displayName: string | null | undefined, email: string): string {
  if (displayName && displayName.trim()) {
    return displayName.trim();
  }
  const local = email.split("@")[0];
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ") || email;
}

/** Deterministic avatar colour so a given person always renders the same hue. */
export function avatarColour(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLOURS[hash % AVATAR_COLOURS.length];
}

/** Build a Person from an instance member-like record. */
export function personFrom(member: { email: string; displayName?: string | null; role?: string }): Person {
  const name = displayNameOf(member.displayName, member.email);
  return { name, role: member.role, initials: initialsOf(name || member.email), colour: avatarColour(member.email) };
}

/** Parse an instant, treating a bare datetime (no zone) as UTC since the API's *Utc fields
 * may serialize without a trailing 'Z'. */
function parseInstant(iso: string): number {
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso);
  return Date.parse(hasZone || !iso.includes("T") ? iso : `${iso}Z`);
}

/** "2 hours ago" style relative time from an ISO timestamp. */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  const then = parseInstant(iso);
  if (Number.isNaN(then)) {
    return "—";
  }
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  const units: [number, string][] = [
    [60, "second"],
    [60, "minute"],
    [24, "hour"],
    [7, "day"],
    [4.345, "week"],
    [12, "month"],
    [Number.POSITIVE_INFINITY, "year"],
  ];
  let value = seconds;
  for (const [step, label] of units) {
    if (value < step) {
      const n = Math.max(1, Math.floor(value));
      return label === "second" && value < 45 ? "just now" : `${n} ${label}${n === 1 ? "" : "s"} ago`;
    }
    value /= step;
  }
  return "just now";
}

/** "31 Mar 2026" date formatting from an ISO date. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) {
    return "";
  }
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * The calendar day a WHOLE-DAY date names, for a value the API stores as UTC midnight — a review date is
 * written by this panel as `<date>T00:00:00.000Z`. Read as an instant it slips to the day before for
 * anyone behind UTC, so pass it through here before formatting or comparing. The date input is
 * repopulated the same way, which is why the two agree.
 */
export function utcDay(iso: string | null | undefined): string | null {
  return iso ? iso.slice(0, 10) : null;
}

/** Whether an ISO date is strictly before today (used for overdue review dates). */
export function isPastDate(iso: string | null | undefined): boolean {
  if (!iso) {
    return false;
  }
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}
